import { Err, Ok, Result } from "ts-results";
import { SafeParseError, SafeParseReturnType, z } from "zod";

// Custom error type that includes sheet and row information
export interface ParseError {
  sheetName: string;
  rowNumber: number;
  zodError: SafeParseError<unknown>;
}

// Helper function to normalize headers
const normalizeString = (header: string): string => {
  return header
    .toLowerCase()
    .trim()
    .replace(/[:.,;!?]+$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

// Helper function to convert Excel date/time to string
const convertExcelDateTimeToString = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  
  if (typeof value === "number") {
    // Excel stores dates as numbers (days since 1900-01-01)
    // For dates: convert to ISO date string
    if (value > 1) {
      const excelEpoch = new Date(1900, 0, 1);
      const date = new Date(excelEpoch.getTime() + (value - 2) * 24 * 60 * 60 * 1000);
      return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
    }
    
    // For times: convert decimal to HH:MM format
    if (value < 1 && value > 0) {
      const totalMinutes = Math.round(value * 24 * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    
    // For other numbers, just convert to string
    return value.toString();
  }
  
  return "";
};

// Custom Zod transform for Excel date/time
const excelDateTimeToString = z.union([
  z.number({ 
    message: "El campo de fecha/hora debe ser un número válido (formato Excel)." 
  }), 
  z.string({ 
    message: "El campo de fecha/hora debe ser una cadena de texto válida." 
  })
], {
  message: "El campo de fecha/hora debe ser un número (formato Excel) o una cadena de texto válida."
}).transform(convertExcelDateTimeToString);

// Custom Zod transform for Excel time
const excelTimeToString = z.union([
  z.number({ 
    message: "El campo de hora debe ser un número válido (formato Excel)." 
  }), 
  z.string({ 
    message: "El campo de hora debe ser una cadena de texto válida." 
  })
], {
  message: "El campo de hora debe ser un número (formato Excel) o una cadena de texto válida."
}).transform(convertExcelDateTimeToString);

// Helper function to normalize row headers
const normalizeRowHeaders = (row: Record<string, unknown>): Record<string, unknown> => {
  const normalizedRow: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    normalizedRow[normalizeString(key)] = value;
  }
  return normalizedRow;
};

// Administrative Data Schema
export const AdministrativeDataSchema = z.object({
  "lugar de emision del informe": z.coerce.string({ 
    message: "El campo 'lugar de emisión del informe' debe ser una cadena de texto válida." 
  }).default(""),
  "razon social": z.coerce.string({ 
    message: "El campo 'razón social' debe ser una cadena de texto válida." 
  }).default(""),
  "registro patronal": z.coerce.string({ 
    message: "El campo 'registro patronal' debe ser una cadena de texto válida." 
  }).default(""),
  "rfc": z.coerce.string({ 
    message: "El campo 'RFC' debe ser una cadena de texto válida." 
  }).default(""),
  "instalaciones": z.coerce.string({ 
    message: "El campo 'instalaciones' debe ser una cadena de texto válida." 
  }).default(""),
  "direccion del sitio del estudio": z.coerce.string({ 
    message: "El campo 'dirección del sitio del estudio' debe ser una cadena de texto válida." 
  }).default(""),
  "actividad": z.coerce.string({ 
    message: "El campo 'actividad' debe ser una cadena de texto válida." 
  }).default(""),
  "persona a dirigir el informe": z.coerce.string({ 
    message: "El campo 'persona a dirigir el informe' debe ser una cadena de texto válida." 
  }).default(""),
  "representante legal": z.coerce.string({ 
    message: "El campo 'representante legal' debe ser una cadena de texto válida." 
  }).default(""),
  "telefono": z.coerce.string({ 
    message: "El campo 'teléfono' debe ser una cadena de texto válida." 
  }).default(""),
  "elaborado por": z.coerce.string({ 
    message: "El campo 'elaborado por' debe ser una cadena de texto válida." 
  }).default(""),
}).transform((data) => {
  const allFieldsEmpty = Object.values(data).every(value => value === "");
  if (allFieldsEmpty) {
    return null;
  }
  return data;
});;

// Recognition Excel Row Schema (Thermal Sources)
export const RecognitionExcelRowSchema = z.object({
  fecha: excelDateTimeToString.default(""),
  departamento: z.coerce.string({ 
    message: "El campo 'departamento' debe ser una cadena de texto válida." 
  }).default(""),
  area: z.coerce.string({ 
    message: "El campo 'área' debe ser una cadena de texto válida." 
  }).default(""),
  "fuentes termicas": z.coerce.string({ 
    message: "El campo 'fuentes térmicas' debe ser una cadena de texto válida." 
  }).default(""),
  "descripcion del espacio": z.preprocess(v => normalizeString(String(v)), z.enum(["abierto", "cerrado"], { 
    message: "El campo 'descripción del espacio' debe ser 'abierto' o 'cerrado'." 
  })).optional(),
  "tipo de ventilacion": z.preprocess(v => normalizeString(String(v)), z.enum(["natural", "artificial", "artificial y natural", "natural y artificial"], { 
    message: "El campo 'tipo de ventilación' debe ser 'natural', 'artificial', 'artificial y natural' o 'natural y artificial'." 
  })).optional(),
  "proceso de produccion": z.coerce.string({ 
    message: "El campo 'proceso de producción' debe ser una cadena de texto válida." 
  }).default(""),
});

// Exposure Groups Excel Row Schema
export const ExposureGroupsExcelRowSchema = z.object({
  "identificacion del grupo de exposicion similar": z.coerce.string({ 
    message: "El campo 'identificación del grupo de exposición similar' debe ser una cadena de texto válida." 
  }).default(""),
  departamento: z.coerce.string({ 
    message: "El campo 'departamento' debe ser una cadena de texto válida." 
  }).default(""),
  area: z.coerce.string({ 
    message: "El campo 'área' debe ser una cadena de texto válida." 
  }).default(""),
  puesto: z.coerce.string({ 
    message: "El campo 'puesto' debe ser una cadena de texto válida." 
  }).default(""),
  poe: z.number({ 
    message: "El campo 'POE' debe ser un número válido." 
  }).int({ 
    message: "El campo 'POE' debe ser un número entero." 
  }).min(0, { 
    message: "El campo 'POE' debe ser un número mayor o igual a 0." 
  }).optional(),
  "descripcion de actividades": z.coerce.string({ 
    message: "El campo 'descripción de actividades' debe ser una cadena de texto válida." 
  }).default(""),
  "tiempo de exposicion (min)": z.number({ 
    message: "El campo 'tiempo de exposición (min)' debe ser un número válido y es requerido." 
  }).min(0, { 
    message: "El campo 'tiempo de exposición (min)' debe ser mayor que 0." 
  }),
  "porcentaje de descanso (por hora)": z.number({ 
    message: "El campo 'porcentaje de descanso (por hora)' debe ser un número válido y es requerido." 
  }).min(0, { 
    message: "El campo 'porcentaje de descanso (por hora)' debe ser mayor que 0." 
  }).max(100, { 
    message: "El campo 'porcentaje de descanso (por hora)' debe ser menor que 100." 
  }),
  "ciclos de exposicion": z.number({ 
    message: "El campo 'ciclos de exposición' debe ser un número válido." 
  }).int({ 
    message: "El campo 'ciclos de exposición' debe ser un número entero." 
  }).min(0, { 
    message: "El campo 'ciclos de exposición' debe ser un número mayor o igual a 0." 
  }).optional(),
  "tipo de puesto": z.coerce.string({ 
    message: "El campo 'tipo de puesto' debe ser una cadena de texto válida." 
  }).default(""),
  "carga solar (si / no)": z.preprocess(v => normalizeString(String(v)), z.enum(["si", "no"], { 
    message: "El campo 'carga solar' debe ser 'sí' o 'no'." 
  })),
  "regimen de trabajo": z.preprocess(v => normalizeString(String(v)), z.enum(["descanso", "ligero", "moderado", "pesado", "muy pesado"], { 
    message: "El campo 'régimen de trabajo' debe ser 'descanso', 'ligero', 'moderado', 'pesado' o 'muy pesado'." 
  })),
  "postura (sentado / de pie)": z.preprocess(v => normalizeString(String(v)), z.enum(["sentado", "de pie"], { 
    message: "El campo 'postura' debe ser 'sentado' o 'de pie'." 
  })).optional(),
  "equipo de proteccion personal": z.coerce.string({ 
    message: "El campo 'equipo de protección personal' debe ser una cadena de texto válida." 
  }).default(""),
  "valor de ajuste por ropa (cav °c)": z.number({ 
    message: "El campo 'valor de ajuste por ropa (cav °c)' debe ser un número válido." 
  }).default(0),
  "medidas de control administrativas": z.coerce.string({ 
    message: "El campo 'medidas de control administrativas' debe ser una cadena de texto válida." 
  }).default(""),
  "medidas de control tecnicas": z.coerce.string({ 
    message: "El campo 'medidas de control técnicas' debe ser una cadena de texto válida." 
  }).default(""),
});

// Body Temperature Excel Row Schema
export const BodyTemperatureExcelRowSchema = z.object({
  departamento: z.coerce.string({ 
    message: "El campo 'departamento' debe ser una cadena de texto válida." 
  }).default(""),
  area: z.coerce.string({ 
    message: "El campo 'área' debe ser una cadena de texto válida." 
  }).default(""),
  identificacion: z.coerce.string({ 
    message: "El campo 'identificación' debe ser una cadena de texto válida." 
  }).default(""),
  "identificacion del grupo de exposicion similar": z.coerce.string({ 
    message: "El campo 'identificación del grupo de exposición similar' debe ser una cadena de texto válida." 
  }).default(""),
  "temperatura axilar inicio del turno (°c)": z.number({ 
    message: "El campo 'temperatura axilar inicio del turno (°C)' debe ser un número válido." 
  }).optional(),
  "temperatura axilar fin del turno (°c)": z.number({ 
    message: "El campo 'temperatura axilar fin del turno (°C)' debe ser un número válido." 
  }).optional(),
  "medicion 1 temperatura axilar inicial (°c)": z.number({ 
    message: "El campo 'medición 1 temperatura axilar inicial (°C)' debe ser un número válido." 
  }).optional(),
  "medicion 1 temperatura axilar final (°c)": z.number({ 
    message: "El campo 'medición 1 temperatura axilar final (°C)' debe ser un número válido." 
  }).optional(),
  "medicion 2 temperatura axilar inicial (°c)": z.number({ 
    message: "El campo 'medición 2 temperatura axilar inicial (°C)' debe ser un número válido." 
  }).optional(),
  "medicion 2 temperatura axilar final (°c)": z.number({ 
    message: "El campo 'medición 2 temperatura axilar final (°C)' debe ser un número válido." 
  }).optional(),
  "medicion 3 temperatura axilar inicial (°c)": z.number({ 
    message: "El campo 'medición 3 temperatura axilar inicial (°C)' debe ser un número válido." 
  }).optional(),
  "medicion 3 temperatura axilar final (°c)": z.number({ 
    message: "El campo 'medición 3 temperatura axilar final (°C)' debe ser un número válido." 
  }).optional(),
  "termometro marca": z.coerce.string({ 
    message: "El campo 'termómetro marca' debe ser una cadena de texto válida." 
  }).default(""),
  "termometro modelo": z.coerce.string({ 
    message: "El campo 'termómetro modelo' debe ser una cadena de texto válida." 
  }).default(""),
  "termometro no. serie": z.coerce.string({ 
    message: "El campo 'termómetro no. serie' debe ser una cadena de texto válida." 
  }).default(""),
});

// Evaluation Excel Row Schema
export const EvaluationExcelRowSchema = z.object({
  "no. medicion": z.coerce.string({ 
    message: "El campo 'no. medición' debe ser una cadena de texto válida." 
  }).default(""),
  observador: z.coerce.string({ 
    message: "El campo 'observador' debe ser una cadena de texto válida." 
  }).default(""),
  observaciones: z.coerce.string({ 
    message: "El campo 'observaciones' debe ser una cadena de texto válida." 
  }).default(""),
  fecha: excelDateTimeToString.default(""),
  "tipo de medicion": z.coerce.string({ 
    message: "El campo 'tipo de medición' debe ser una cadena de texto válida." 
  }).default(""),
  departamento: z.coerce.string({ 
    message: "El campo 'departamento' debe ser una cadena de texto válida." 
  }).default(""),
  area: z.coerce.string({ 
    message: "El campo 'área' debe ser una cadena de texto válida." 
  }).default(""),
  ubicacion: z.coerce.string({ 
    message: "El campo 'ubicación' debe ser una cadena de texto válida." 
  }).default(""),
  identificacion: z.coerce.string({ 
    message: "El campo 'identificación' debe ser una cadena de texto válida." 
  }).default(""),
  puesto: z.coerce.string({ 
    message: "El campo 'puesto' debe ser una cadena de texto válida." 
  }).default(""),
  "limite maximo permitido (°c)": z.number({ 
    message: "El campo 'límite máximo permitido (°C)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'límite máximo permitido (°C)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'límite máximo permitido (°C)' debe ser menor o igual a 70." 
  }).optional(),
  "nivel de accion (°c)": z.number({ 
    message: "El campo 'nivel de acción (°C)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'nivel de acción (°C)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'nivel de acción (°C)' debe ser menor o igual a 70." 
  }).optional(),
  "temperatura axilar inicial (°c)": z.number({ 
    message: "El campo 'temperatura axilar inicial (°C)' debe ser un número válido." 
  }).min(34, { 
    message: "El campo 'temperatura axilar inicial (°C)' debe ser mayor o igual a 34." 
  }).max(45, { 
    message: "El campo 'temperatura axilar inicial (°C)' debe ser menor o igual a 45." 
  }).optional(),
  "temperatura axilar final (°c)": z.number({ 
    message: "El campo 'temperatura axilar final (°C)' debe ser un número válido." 
  }).min(34, { 
    message: "El campo 'temperatura axilar final (°C)' debe ser mayor o igual a 34." 
  }).max(45, { 
    message: "El campo 'temperatura axilar final (°C)' debe ser menor o igual a 45." 
  }).optional(),
  "equipo marca": z.coerce.string({ 
    message: "El campo 'equipo marca' debe ser una cadena de texto válida." 
  }).default(""),
  "equipo modelo": z.coerce.string({ 
    message: "El campo 'equipo modelo' debe ser una cadena de texto válida." 
  }).default(""),
  "equipo no. serie": z.coerce.string({ 
    message: "El campo 'equipo no. serie' debe ser una cadena de texto válida." 
  }).default(""),
  "factor de incertidumbre": z.number({ 
    message: "El campo 'factor de incertidumbre' debe ser un número válido." 
  }).optional(),
  "horario de medicion 1": excelTimeToString.default(""),
  "tobillos bulbo humedo (°c) (1 / 3)": z.number({ 
    message: "El campo 'tobillos bulbo húmedo (°C) (1/3)' debe ser un número válido y es requerido." 
  }).min(10, { 
    message: "El campo 'tobillos bulbo húmedo (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos bulbo húmedo (°C) (1/3)' debe ser menor o igual a 70." 
  }),
  "tobillos bulbo seco (°c) (1 / 3)": z.number({ 
    message: "El campo 'tobillos bulbo seco (°C) (1/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos bulbo seco (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos bulbo seco (°C) (1/3)' debe ser menor o igual a 70." 
  }).optional(),
  "tobillos globo (°c) (1 / 3)": z.number({ 
    message: "El campo 'tobillos globo (°C) (1/3)' debe ser un número válido y es requerido." 
  }).min(10, { 
    message: "El campo 'tobillos globo (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos globo (°C) (1/3)' debe ser menor o igual a 70." 
  }),
  "abdomen bulbo humedo (°c) (1 / 3)": z.number({ 
    message: "El campo 'abdomen bulbo húmedo (°C) (1/3)' debe ser un número válido y es requerido." 
  }).min(10, { 
    message: "El campo 'abdomen bulbo húmedo (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen bulbo húmedo (°C) (1/3)' debe ser menor o igual a 70." 
  }),
  "abdomen bulbo seco (°c) (1 / 3)": z.number({ 
    message: "El campo 'abdomen bulbo seco (°C) (1/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen bulbo seco (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen bulbo seco (°C) (1/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen globo (°c) (1 / 3)": z.number({ 
    message: "El campo 'abdomen globo (°C) (1/3)' debe ser un número válido y es requerido." 
  }).min(10, { 
    message: "El campo 'abdomen globo (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen globo (°C) (1/3)' debe ser menor o igual a 70." 
  }),
  "cabeza bulbo humedo (°c) (1 / 3)": z.number({ 
    message: "El campo 'cabeza bulbo húmedo (°C) (1/3)' debe ser un número válido y es requerido." 
  }).min(10, { 
    message: "El campo 'cabeza bulbo húmedo (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza bulbo húmedo (°C) (1/3)' debe ser menor o igual a 70." 
  }),
  "cabeza bulbo seco (°c) (1 / 3)": z.number({ 
    message: "El campo 'cabeza bulbo seco (°C) (1/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza bulbo seco (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza bulbo seco (°C) (1/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza globo (°c) (1 / 3)": z.number({ 
    message: "El campo 'cabeza globo (°C) (1/3)' debe ser un número válido y es requerido." 
  }).min(10, { 
    message: "El campo 'cabeza globo (°C) (1/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza globo (°C) (1/3)' debe ser menor o igual a 70." 
  }),
  "horario de medicion 2": excelTimeToString.default(""),
  "tobillos bulbo humedo (°c) (2 / 3)": z.number({ 
    message: "El campo 'tobillos bulbo húmedo (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos bulbo húmedo (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos bulbo húmedo (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "tobillos bulbo seco (°c) (2 / 3)": z.number({ 
    message: "El campo 'tobillos bulbo seco (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos bulbo seco (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos bulbo seco (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "tobillos globo (°c) (2 / 3)": z.number({ 
    message: "El campo 'tobillos globo (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos globo (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos globo (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen bulbo humedo (°c) (2 / 3)": z.number({ 
    message: "El campo 'abdomen bulbo húmedo (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen bulbo húmedo (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen bulbo húmedo (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen bulbo seco (°c) (2 / 3)": z.number({ 
    message: "El campo 'abdomen bulbo seco (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen bulbo seco (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen bulbo seco (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen globo (°c) (2 / 3)": z.number({ 
    message: "El campo 'abdomen globo (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen globo (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen globo (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza bulbo humedo (°c) (2 / 3)": z.number({ 
    message: "El campo 'cabeza bulbo húmedo (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza bulbo húmedo (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza bulbo húmedo (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza bulbo seco (°c) (2 / 3)": z.number({ 
    message: "El campo 'cabeza bulbo seco (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza bulbo seco (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza bulbo seco (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza globo (°c) (2 / 3)": z.number({ 
    message: "El campo 'cabeza globo (°C) (2/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza globo (°C) (2/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza globo (°C) (2/3)' debe ser menor o igual a 70." 
  }).optional(),
  "horario de medicion 3": excelTimeToString.default(""),
  "tobillos bulbo humedo (°c) (3 / 3)": z.number({ 
    message: "El campo 'tobillos bulbo húmedo (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos bulbo húmedo (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos bulbo húmedo (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "tobillos bulbo seco (°c) (3 / 3)": z.number({ 
    message: "El campo 'tobillos bulbo seco (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos bulbo seco (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos bulbo seco (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "tobillos globo (°c) (3 / 3)": z.number({ 
    message: "El campo 'tobillos globo (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'tobillos globo (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'tobillos globo (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen bulbo humedo (°c) (3 / 3)": z.number({ 
    message: "El campo 'abdomen bulbo húmedo (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen bulbo húmedo (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen bulbo húmedo (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen bulbo seco (°c) (3 / 3)": z.number({ 
    message: "El campo 'abdomen bulbo seco (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen bulbo seco (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen bulbo seco (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "abdomen globo (°c) (3 / 3)": z.number({ 
    message: "El campo 'abdomen globo (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'abdomen globo (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'abdomen globo (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza bulbo humedo (°c) (3 / 3)": z.number({ 
    message: "El campo 'cabeza bulbo húmedo (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza bulbo húmedo (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza bulbo húmedo (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza bulbo seco (°c) (3 / 3)": z.number({ 
    message: "El campo 'cabeza bulbo seco (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza bulbo seco (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza bulbo seco (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
  "cabeza globo (°c) (3 / 3)": z.number({ 
    message: "El campo 'cabeza globo (°C) (3/3)' debe ser un número válido." 
  }).min(10, { 
    message: "El campo 'cabeza globo (°C) (3/3)' debe ser mayor o igual a 10." 
  }).max(70, { 
    message: "El campo 'cabeza globo (°C) (3/3)' debe ser menor o igual a 70." 
  }).optional(),
});

// Types
export type AdministrativeData = z.infer<typeof AdministrativeDataSchema>;
export type RecognitionExcelRow = z.infer<typeof RecognitionExcelRowSchema>;
export type ExposureGroupsExcelRow = z.infer<typeof ExposureGroupsExcelRowSchema>;
export type BodyTemperatureExcelRow = z.infer<typeof BodyTemperatureExcelRowSchema>;
export type EvaluationExcelRow = z.infer<typeof EvaluationExcelRowSchema>;

export const ExcelDataSchema = z.object({
  administrativeData: AdministrativeDataSchema.nullable(),
  recognitionRows: z.array(RecognitionExcelRowSchema, { 
    message: "El campo 'fuentes térmicas' debe ser un arreglo válido de filas de reconocimiento." 
  }),
  exposureGroupsRows: z.array(ExposureGroupsExcelRowSchema, { 
    message: "El campo 'grupos de exposición' debe ser un arreglo válido de filas de grupos de exposición." 
  }),
  bodyTemperatures: z.array(BodyTemperatureExcelRowSchema, { 
    message: "El campo 'temperatura corporal' debe ser un arreglo válido de filas de temperatura corporal." 
  }),
  evaluationRows: z.array(EvaluationExcelRowSchema, { 
    message: "El campo 'evaluaciones' debe ser un arreglo válido de filas de evaluación." 
  }).min(1, { 
    message: "Su archivo debe tener al menos una evaluación." 
  }),
}, {
  message: "Debe subir al menos un archivo antes de generar el reporte."
});

export type ExcelData = z.infer<typeof ExcelDataSchema>;

// Helper function to parse Excel data
export const parseExcelData = async (file: File): Promise<Result<ExcelData, ParseError[]>> => {
  const XLSX = await import('xlsx');
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data);

  const administrativeDataKey = workbook.SheetNames.find(sheetName => normalizeString(sheetName) === 'portada');
  const recognitionKey = workbook.SheetNames.find(sheetName => normalizeString(sheetName) === 'fuentes termicas');
  const exposureGroupsKey = workbook.SheetNames.find(sheetName => normalizeString(sheetName) === 'grupos de exposicion');
  const bodyTemperatureKey = workbook.SheetNames.find(sheetName => normalizeString(sheetName) === 'temperatura corporal');
  const evaluationsKey = workbook.SheetNames.find(sheetName => normalizeString(sheetName) === 'evaluaciones');

  let administrativeDataResult: SafeParseReturnType<Record<string, unknown>, AdministrativeData> = AdministrativeDataSchema.safeParse({});
  const errors: ParseError[] = [];
  const recognitionRows: RecognitionExcelRow[] = [];
  const exposureGroupsRows: ExposureGroupsExcelRow[] = [];
  const bodyTemperatureRows: BodyTemperatureExcelRow[] = [];
  const evaluationRows: EvaluationExcelRow[] = [];

  if (administrativeDataKey) {
    const sheet = workbook.Sheets[administrativeDataKey];
    // Parse as array of arrays since the structure is key-value pairs
    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][];
    
    // Transform array of arrays into key-value object
    const administrativeDataObj: Record<string, unknown> = {};
    for (const row of rawData) {
      if (row[0] && row[1]) {
        const key = normalizeString(String(row[0]));
        const value = row[1];
        administrativeDataObj[key] = value;
      }
    }
    
    administrativeDataResult = AdministrativeDataSchema.safeParse(administrativeDataObj);
    if (!administrativeDataResult.success) {
      errors.push({
        sheetName: administrativeDataKey,
        rowNumber: 0, // Administrative data doesn't have specific row numbers
        zodError: administrativeDataResult
      });
    }
  }

  if (recognitionKey) {
    const sheet = workbook.Sheets[recognitionKey];
    const json = XLSX.utils.sheet_to_json(sheet) as Record<string, unknown>[];
    json.forEach((row, index) => {
      const result = RecognitionExcelRowSchema.safeParse(normalizeRowHeaders(row));
      if (result.success) {
        recognitionRows.push(result.data);
      } else {
        errors.push({
          sheetName: recognitionKey,
          rowNumber: index + 2, // +2 because Excel is 1-indexed and first row is headers
          zodError: result
        });
      }
    });
  }

  if (exposureGroupsKey) {
    const sheet = workbook.Sheets[exposureGroupsKey];
    const json = XLSX.utils.sheet_to_json(sheet) as Record<string, unknown>[];
    json.forEach((row, index) => {
      const result = ExposureGroupsExcelRowSchema.safeParse(normalizeRowHeaders(row));
      if (result.success) {
        exposureGroupsRows.push(result.data);
      } else {
        errors.push({
          sheetName: exposureGroupsKey,
          rowNumber: index + 2, // +2 because Excel is 1-indexed and first row is headers
          zodError: result
        });
      }
    });
  }

  if (bodyTemperatureKey) {
    const sheet = workbook.Sheets[bodyTemperatureKey];
    const json = XLSX.utils.sheet_to_json(sheet) as Record<string, unknown>[];
    json.forEach((row, index) => {
      const result = BodyTemperatureExcelRowSchema.safeParse(normalizeRowHeaders(row));
      if (result.success) {
        bodyTemperatureRows.push(result.data);
      } else {
        errors.push({
          sheetName: bodyTemperatureKey,
          rowNumber: index + 2, // +2 because Excel is 1-indexed and first row is headers
          zodError: result
        });
      }
    });
  }

  if (evaluationsKey) {
    const sheet = workbook.Sheets[evaluationsKey];
    const json = XLSX.utils.sheet_to_json(sheet) as Record<string, unknown>[];
    json.forEach((row, index) => {
      const result = EvaluationExcelRowSchema.safeParse(normalizeRowHeaders(row));
      if (result.success) {
        evaluationRows.push(result.data);
      } else {
        errors.push({
          sheetName: evaluationsKey,
          rowNumber: index + 2, // +2 because Excel is 1-indexed and first row is headers
          zodError: result
        });
      }
    });
  }

  if (errors.length > 0) {
    return Err(errors);
  }

  // Ensure administrative data is successful
  if (!administrativeDataResult?.success) {
    return Err([{
      sheetName: administrativeDataKey || 'portada',
      rowNumber: 0,
      zodError: administrativeDataResult as SafeParseError<AdministrativeData>
    }]);
  }

  return Ok({
    administrativeData: administrativeDataResult.data,
    recognitionRows,
    exposureGroupsRows,
    bodyTemperatures: bodyTemperatureRows,
    evaluationRows,
  });
};
